import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { FormService } from 'src/app/shared/services/form.service';
import { WorksheetResponse } from 'src/app/features/worksheet/models/worksheet-response.modal';
import { MatDialog } from '@angular/material/dialog';
import { NotesComponent } from 'src/app/shared/components/notes/notes.component';
import { InstructionsComponent } from 'src/app/shared/components/instructions/instructions.component';
import { ProjectDetailsComponent } from 'src/app/shared/components/project-details/project-details.component';
import { MatTableDataSource } from '@angular/material/table';
import { AssignToLabtechComponent } from 'src/app/shared/components/assign-to-labtech/assign-to-labtech.component';
import { UserService } from 'src/app/shared/services/user.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { INotes } from 'src/app/features/models/inotes.modal';
import { SharedService } from 'src/app/features/service/shared.service';
import { NativeSoilService } from 'src/app/features/worksheet/services/native-soil/native-soil.service';
import { NativeSoilType, CompactionWorkSheetPatchDto } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { DialogService } from 'src/app/shared/services/dialog.service';

@Component({
    selector: 'app-compaction-worksheet',
    templateUrl: './compaction-worksheet.component.html',
    styleUrls: ['./compaction-worksheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompactionWorksheetComponent {
@Output() assigned = new EventEmitter<boolean>();
  @Output() rowDeletionSuccess = new EventEmitter<void>();
  selection = new SelectionModel<NativeSoilType>(true,[]);
  
  hasUnsavedChanges = false;
  projectId: string | null = null;
  private readonly destroyed$ = new Subject<boolean>();
  public dataSource = new MatTableDataSource<NativeSoilType>();
  public worksheets: NativeSoilType[];
  public worksheetRecords: WorksheetResponse | null = null;
  private originalWorksheetData: NativeSoilType[] = [];
  public note: INotes | undefined;
  userRoles: string[];
  daysLeft: number = 0;
  noteText: string | undefined = '';
  columns = [
    { key: 'select', displayName: '' },
    { key: 'sampleId', displayName: 'Sample Id' },
    { key: 'effort', displayName: 'Compaction Effort' },
    { key: 'depth', displayName: 'Depth' },
    { key: 'labSpecMoistureContent', displayName: 'Lab Spec Moisture Content' },
    { key: 'inHouseId', displayName: 'In-House Id' },
    { key: 'mouldPlusProbeTare', displayName: 'Mold + Probe Weight' },
    { key: 'soilDiameter', displayName: 'Sample Diameter' },
    { key: 'tubeHeight', displayName: 'Sample Height' },
    { key: 'minus', displayName: 'Minus' },
    { key: 'weightPerLayer', displayName: 'Weight per Layer' },
    { key: 'targetWeight', displayName: 'Target Weight' },
    { key: 'wetPlusTare', displayName: 'Actual Weight' },
    { key: 'wetTr', displayName: 'TR' },
    { key: 'geothermSoilDescription', displayName: 'Geotherm Soil Description' },
    { key: 'isCompactionCompleted', displayName: 'Status' },
    { key: 'assignedTo', displayName: 'Assigned To' },
    { key: 'action', displayName: '' }
  ];

  displayedColumns = this.columns.map(c => c.key);
  rowData: NativeSoilType[];
  response: any;
  isInstructionsConfirmed: boolean | null = null;

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private worksheetService: WorksheetService,
    private formService: FormService,
    private sharedService: SharedService,
    public dialog: MatDialog,
    public userService: UserService,
    private snackBar: MatSnackBar,
    private nativeSoilService: NativeSoilService,
    private dialogService: DialogService
  ) { }
  
  canDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges) {
      return new Observable<boolean>(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'Compaction-Worksheet',
      data: {
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to leave without saving?',
        cancelText: 'Stay',
        confirmText: 'Leave'
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    return dialogRef.afterClosed().pipe(
      map(result => result === 'YES')
    );
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    this.fetchWorksheet(this.projectId ?? '');
    this.fetchWorksheetNotes(this.projectId ?? '');
    this.fetchWorksheetDetails(this.projectId ?? '');
    this.userAccountAndRole();
  }

  viewSubmissionForm(): void {
    const params = {
      f: 'w',
    };
    const navigationExtras: NavigationExtras = {
      queryParams: params,
      state: {
        isShipmentReceived: this.worksheetRecords?.project?.storageCompartment
      }
    };

    this.formService.updateData(null);
    this.router.navigate([`/review/form/nativesoil/${this.projectId}`], navigationExtras);
  }

  userAccountAndRole() {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
      }
    });
  }

  isAdmin(): boolean {
    return this.userRoles?.includes('Admin');
  }

  toggleSelection(row: NativeSoilType) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  isIndeterminate() {
    return this.selection.selected.length > 0 && !this.isAllSelected();
  }

  isMultipleSelected(row: any): boolean {
    return this.selection.selected.length >= 1 && this.selection.selected.includes(row);
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  isFirstRowOfSample(row: NativeSoilType, index: number): boolean {
    if (index === 0) {
      return true;
    }

    return this.dataSource.data[index - 1].nativeSoilSampleId !== row.nativeSoilSampleId;
  }

  copyDescriptionToGroup(row: NativeSoilType) {
    this.dataSource.data
        .filter(x => x.nativeSoilSampleId === row.nativeSoilSampleId)
        .forEach(x => {
            x.geothermSoilDescription = row.geothermSoilDescription;
        });

    this.hasUnsavedChanges = true;
  }

  // The description input only renders on the first row of each sample, so jump to the next rendered one
  focusNextDescription(event: Event): void {
    event.preventDefault();
    const inputs = Array.from(
      document.querySelectorAll('[data-type="geothermSoilDescription"]')
    ) as HTMLInputElement[];
    const nextInput = inputs[inputs.indexOf(event.target as HTMLInputElement) + 1];

    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  }

  isLastRowOfEffort(row: NativeSoilType, index: number): boolean {
    const nextRow = this.dataSource.data[index + 1];
    if (!nextRow) return false;

    return (
      nextRow.nativeSoilSampleId === row.nativeSoilSampleId &&
      nextRow.effort !== row.effort
    );
  }

  isLastRowOfSample(row: NativeSoilType, index: number): boolean {
    const currentSampleId = row.nativeSoilSampleId;
    const nextRow = this.dataSource.data[index + 1];
    if (!nextRow) return false;

    return nextRow.nativeSoilSampleId !== currentSampleId;
  }

  onFocusClearZero(obj: any, field: string) {
    if (obj[field] === 0) {
      obj[field] = null;
    }
  }

  onBlurRestoreZero(obj: any, field: string) {
    if (obj[field] === null || obj[field] === '') {
      obj[field] = 0;
    }
  }

  focusNextRow(event: Event, currentRowId: string, fieldType: string): void {
    event.preventDefault();
    const currentIndex = this.dataSource.data.findIndex(
      x => x.id === currentRowId
    );
    if (currentIndex === -1) { return; }
    const nextRow = this.dataSource.data[currentIndex + 1];
    if (!nextRow) { return; }

    setTimeout(() => {
      const nextInput = document.querySelector(
        `[data-row-id="${nextRow.id}"][data-type="${fieldType}"]`
      ) as HTMLInputElement;

      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    });
  }

  saveTable(): void {
    const payload: CompactionWorkSheetPatchDto[] = [];

    for (const currentRow of this.dataSource.data) {
      const originalRow = this.originalWorksheetData.find(
        original => original.id === currentRow.id
      );

      if (!originalRow) { continue; }
      const changes = this.getChangedWorksheetProperties(
        originalRow,
        currentRow
      );

      if (Object.keys(changes).length === 0) { continue; }
      payload.push({
        id: currentRow.id!,
        projectId: currentRow.projectId ?? this.projectId!,
        ...changes
      });
    }

    if (payload.length === 0) {
      this.snackBar.open('No changes to save', 'OK', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['default-snackbar'],
      });

      return;
    }

    this.worksheetService.saveCompactionWorksheetTable(payload).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;

        this.snackBar.open('Saved successfully', 'DONE', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['default-snackbar'],
        });

        this.ngOnInit();
      },
      error: () => {
        this.dialogService.open(messagesConfig.saveTableError);
      }
    });
  }

  private getChangedWorksheetProperties(original: any, current: any): Partial<CompactionWorkSheetPatchDto> {
    const changes: Partial<CompactionWorkSheetPatchDto> = {};

    if (original.inHouseId !== current.inHouseId) { changes.inhouseId = current.inHouseId; }
    if (Number(original.mouldPlusProbeTare ?? 0) !== Number(current.mouldPlusProbeTare ?? 0)) {
      changes.mouldPlusProbeTare =  Number(current.mouldPlusProbeTare ?? 0);
    }

    if (Number(original.soilDiameter ?? 0) !== Number(current.soilDiameter ?? 0)) {
      changes.soilDiameter = Number(current.soilDiameter ?? 0);
    }

    if (Number(original.tubeHeight ?? 0) !== Number(current.tubeHeight ?? 0)) {
      changes.tubeHeight = Number(current.tubeHeight ?? 0);
    }

    if (Number(original.minus ?? 0) !== Number(current.minus ?? 0)) {
      changes.minus = Number(current.minus ?? 0);
    }

    const originalWeightPerLayer =  this.calculateWeightPerLayer(original);
    const currentWeightPerLayer = this.calculateWeightPerLayer(current);

    if (Number(originalWeightPerLayer ?? 0) !== Number(currentWeightPerLayer ?? 0)) {
      changes.weightPerLayer = currentWeightPerLayer;
    }

    if (Number(original.wetPlusTare ?? 0) !== Number(current.wetPlusTare ?? 0)) {
      changes.wetPlusTare = Number(current.wetPlusTare ?? 0);
    }

    if (Number(original.wetTr ?? 0) !== Number(current.wetTr ?? 0)) {
      changes.wetTr = Number(current.wetTr ?? 0);
    }

    if (original.geothermSoilDescription !== current.geothermSoilDescription) {
      changes.geothermSoilDescription = current.geothermSoilDescription;
    }

    if (original.isCompactionCompleted !== current.isCompactionCompleted) {
      changes.isCompactionCompleted = current.isCompactionCompleted;
    }

    return changes;
  }

  fetchWorksheetNotes(projectId: string): void {
    this.sharedService.fetchWSNotes(projectId);
    this.sharedService.notes$.subscribe((data: any) => {
      this.note = data;
    });
    this.sharedService.notesText$.subscribe((data: string | undefined) => {
      this.noteText = data;
    });
  }

  fetchWorksheetDetails(projectId: string): void {
    this.formService.fetchFormData(projectId, 'nativesoil').subscribe({
      next: (result) => {
        const project = result?.project;
        const company = result?.company;
        const comments = result?.comments;
        this.worksheetRecords = {
          project,
          company,
          comments
        };
        if(this.worksheetRecords.project.dueDate) {
          const dueDate = new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(this.worksheetRecords.project.dueDate));
          this.daysLeft = this.findDateDifference(new Date(), dueDate);
        }
        this.isInstructionsConfirmed = this.worksheetRecords.project?.instructionsConfirmed ?? false;
      },
    });
  }

  updateCalculations(element: any, pageLoad: boolean = false) {
    if (!pageLoad) {
      this.hasUnsavedChanges = true;
    }

    element.weightPerLayer = this.calculateWeightPerLayer(element);
    element.targetWeight = this.calculateTargetWeight(element);

    if (!this.canCompleteRow(element)) {
      element.isCompactionCompleted = false;
    }
  }

  calculateWeightPerLayer(element: any): number {
    const sampleDiameter = Number(element.soilDiameter ?? 0);
    const sampleHeight = Number(element.tubeHeight ?? 0);
    const minus = Number(element.minus ?? 0);
    const pi = 3.14159;
    const mouldVolume = ((pi * sampleDiameter * sampleDiameter) / 4000) * (sampleHeight - minus);

    console.log(element);
    console.log('INPUT', {
      sampleId: element.sampleId,
      soilDiameter: element.soilDiameter,
      tubeHeight: element.tubeHeight,
      minus: element.minus,
      effort: element.effort,
      labSpecMoistureContent: element.labSpecMoistureContent,
      clientSpecifiedMoistureContent: element.clientSpecifiedMoistureContent,
      singleProctorDryDensity: element.singleProctorDryDensity
    });

    if(!element.clientSpecifiedMoistureContent && !element.labSpecMoistureContent && element.singleProctorDryDensity) {
        const weightperlayer = (((Number(element.singleProctorDryDensity) * (Number(element.effort) / 100)) / 62.4) * mouldVolume) / 5
        console.log('Calculating weight per layer using single proctor dry density', {
            singleProctorDryDensity: element.singleProctorDryDensity,
            effort: element.effort,
            mouldVolume: mouldVolume
        });
        return weightperlayer;
    }

    const proctorDryDensity = this.response?.moistureWorksheets?.find((mw: any) => mw.nativeSoilSampleId === element.nativeSoilSampleId)?.proctorDryDensity ?? 0;
    const bulkDensity = proctorDryDensity * (1 + element.labSpecMoistureContent / 100);
    const weightPerLayer = ((mouldVolume * bulkDensity * element.effort) / 100 / 62.4 / 5);
    console.log('CAL-OUTPUT', {
      proctorDryDensity: proctorDryDensity,
      bulkDensity: bulkDensity,
      weightPerLayer: weightPerLayer
    });

    return weightPerLayer;
  }

  calculateTargetWeight(element: any): number {
    if (!element.weightPerLayer) return 0;
    const drySoilWeight = (element.weightPerLayer * 5) / (1 + element.labSpecMoistureContent / 100);
    const targetWeight = drySoilWeight * (1 + element.labSpecMoistureContent / 100) + Number(element.mouldPlusProbeTare ?? 0);
    return Math.round(targetWeight);
  }

  openViewNotesDialog(): void {
    const messageObject = {
      projectId: this.projectId,
      notes: this.note,
    };
    this.dialog
      .open(NotesComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetNotes(this.projectId ?? '');
      });
  }

  openViewInstructionsDialog(): void {
    if (!this.worksheetRecords?.comments) return;

    this.dialog.open(InstructionsComponent, {
      panelClass: 'app-dialog',
      data: this.worksheetRecords.comments,
      disableClose: true,
      width: '50%',
    });
  }

  openProjectDetailsComponent(title: string, fieldName: string): void {
    if (!this.worksheetRecords?.project) return;
    const project = this.worksheetRecords.project;
    const messageObject = {
      projectId: this.projectId,
      title,
      fieldName,
      ovenDate: project.ovenDate,
      wetTestData: project.wetTestData,
      dryTestData: project.dryTestData,
    };
    this.dialog
      .open(ProjectDetailsComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetDetails(this.projectId ?? '');
      });
  }

  openAssignToDialog(worksheetId: string): void {
    if (this.hasUnsavedChanges) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'worksheets',
        data: {
          title: 'Unsaved Changes',
          content: 'You have unsaved changes. Do you want to continue assigning without saving your changes?',
          cancelText: 'Cancel',
          confirmText: 'Continue'
        },
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'YES') {
          this.openAssignDialogInternal(worksheetId);
        }
      });
      return;
    }
    
    this.openAssignDialogInternal(worksheetId);
  }

  openAssignDialogInternal(worksheetId: string): void {
    this.dialog
      .open(AssignToLabtechComponent, {
        data: {
          worksheetId,
          type: 'workSheet',
          multipleSelected : this.isMultipleSelected(worksheetId),
          worksheetRows : this.selection.selected,
          totalNumOfWorksheets: this.dataSource.data.length,
          role: this.userRoles,
          // Recorded in the Compaction history (the TR worksheet assigns the same rows)
          source: 'Compaction'
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((assigned) => {
        if (assigned) {
          this.fetchWorksheet(this.projectId ?? '');
          this.rowDeletionSuccess.emit();
          this.assigned.emit(true);
          this.selection.clear();
        }
      });
  }

  findDateDifference(startDate: Date, endDate: Date) {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return Math.trunc(dayDifference);
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  setDepth(elem: any): string {
    let depthRange = '';
    if (elem.depth !== null) {
      depthRange = `${elem.depth}`;
    }
    if (elem.endDepth) {
      if (elem.depth !== null && elem.endDepth === null) {
        depthRange = `${elem.depth}`;
      }
      if (elem.endDepth !== null && elem.depth !== null) {
        depthRange = `${elem.depth}-${elem.endDepth}`;
      }
    }
    return depthRange;
  }

  private mapUser(worksheet: any): string | null {
    if (worksheet.assignedToId) {
      return `${worksheet.assignedTo.firstName} ${worksheet.assignedTo.lastName}`;
    }
    return null;
  }

  isActualWeightInRange(element: any): boolean {
    const actualWeight = Number(element.wetPlusTare ?? 0);
    const targetWeight = Number(element.targetWeight ?? 0);

    return actualWeight > 0 &&
          targetWeight > 0 &&
          Math.abs(actualWeight - targetWeight) <= 2;
  }

  isActualWeightOutOfRange(element: any): boolean {
    const actualWeight = Number(element.wetPlusTare ?? 0);
    const targetWeight = Number(element.targetWeight ?? 0);

    return actualWeight > 0 &&
          targetWeight > 0 &&
          Math.abs(actualWeight - targetWeight) > 7;
  }

  isActualWeightWarning(element: any): boolean {
    const actualWeight = Number(element.wetPlusTare ?? 0);
    const targetWeight = Number(element.targetWeight ?? 0);

    const difference = Math.abs(actualWeight - targetWeight);

    return actualWeight > 0 &&
          targetWeight > 0 &&
          difference > 2 &&
          difference <= 7;
  }

  printTargetWeights() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/native-soil-print'],
        { queryParams: { projectId: this.projectId } }
      )
    );

    window.open(url, '_blank');
  }

  preventNumberInputChange(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  asReceivedWorksheet(): void {
    this.navigateToWorksheet('as-received-worksheet');
  }

  moistureConditioningWorksheet(): void {
    this.navigateToWorksheet('moisture-conditioning-worksheet');
  }

  singleProctorWorksheet(): void {
    this.navigateToWorksheet('single-proctor-worksheet');
  }

  compactionWorksheet(): void {
    this.navigateToWorksheet('compaction-worksheet');
  }

  compactionHistory(): void {
    this.navigateToWorksheet('compaction-history');
  }

  thermalResistivityWorksheet(): void {
    this.navigateToWorksheet('native-soil-worksheet');
  }

  private navigateToWorksheet(route: string): void {
    this.canDeactivate().subscribe((canLeave: boolean) => {
      if (canLeave) {
        this.openWorksheet(route);
      }
    });
  }

  openWorksheet(route: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [route],
        {
          queryParams: {
            projectId: this.projectId
          }
        }
      )
    );

    window.location.href = url;
  }

  canCompleteRow(row: any): boolean {
    const mouldPlusProbeTare = Number(row.mouldPlusProbeTare ?? 0);
    const soilDiameter = Number(row.soilDiameter ?? 0);
    const tubeHeight = Number(row.tubeHeight ?? 0);
    const actualWeight = Number(row.wetPlusTare ?? 0);

    return !!row.inHouseId
      && (row.worksheetType === 'Tube' || mouldPlusProbeTare > 0)
      && soilDiameter > 0
      && tubeHeight > 0
      && actualWeight > 0;
  }

  completeRow(row: any, event: any): void {
    row.isCompactionCompleted = event.checked;
    this.saveTable();
  }

  isMoistureWorksheetCompleted(row: NativeSoilType): boolean {
    if(row.worksheetType === 'Tube') {
      return true;
    }
    const moistureWorksheets = this.response?.moistureWorksheets ?? [];
    if (moistureWorksheets.length === 0) {
      return true;
    }

    const moistureWorksheet = moistureWorksheets.find(
      (mw: any) => mw.nativeSoilSampleId === row.nativeSoilSampleId && mw.compactionEffort === row.effort
    );

    return moistureWorksheet?.status === true;
  }

  fetchWorksheet(projectId: string): void {
    this.nativeSoilService.getNativeSoilWorkSheetWillAllMoistureSheets(projectId).subscribe({
      next: (response: any) => {
        let initial = 1;
        this.response = response;
        console.log(response, 'worksheet api response');
        const moistureWorksheets = response?.moistureWorksheets ?? [];
        this.worksheets = (response?.nativeSoilWorksheets || []).filter((x: { reUse: any; }) => !x.reUse);
        this.worksheets.forEach((worksheet: any) => {
          if (!worksheet.groupOrder) {
            initial += 1;
          }
          worksheet.sequence = initial;
          if (worksheet.collectionDate) {
            worksheet.collectionDate = DateTimeUtils.formatDateAndTime(worksheet.collectionDate, 'MM/dd/yyyy');
          }
          worksheet.displayDepth = this.setDepth(worksheet);
          worksheet.assignedTo = this.mapUser(worksheet);
          worksheet.wetPlusTare = worksheet.nativeSoilWorkSheetTestResults.find((x: { testNumber: number; }) => x.testNumber === 1)?.weight ?? 0;
          worksheet.wetTr = worksheet.nativeSoilWorkSheetTestResults.find((x: { testNumber: number; }) => x.testNumber === 1)?.tr ?? 0;
        });
        this.worksheets.sort((a: any, b: any) => {
           // 1. Native worksheets always come before Tube worksheets
          if (a.worksheetType !== b.worksheetType) {
            if (a.worksheetType === 'Bulk') return -1;
            if (b.worksheetType === 'Bulk') return 1;
          }

          // ============================================================
          // 2. NATIVE WORKSHEET SORTING
          // ============================================================
          if (a.worksheetType === 'Bulk' && b.worksheetType === 'Bulk') {
            // 1. Sample first
            const sampleCompare = String(a.sampleId ?? '')
              .localeCompare(String(b.sampleId ?? ''));

            if (sampleCompare !== 0) {
              return sampleCompare;
            }

            // 2. Depth
            const depthCompare = Number(a.depth ?? 0) - Number(b.depth ?? 0);
            if (depthCompare !== 0) {
              return depthCompare;
            }

            // 2. For the same Sample ID, group by Native Soil Sample ID
            const nativeSampleCompare = String(a.nativeSoilSampleId ?? '')
              .localeCompare(
                String(b.nativeSoilSampleId ?? ''),
                undefined,
                { numeric: true, sensitivity: 'base' }
              );

            if (nativeSampleCompare !== 0) {
              return nativeSampleCompare;
            }

            // 2. Compaction effort
            const effortCompare = Number(a.effort) - Number(b.effort);

            if (effortCompare !== 0) {
              return effortCompare;
            }

            // 4. Moisture - DESCENDING
            const moistureCompare =
              Number(b.labSpecMoistureContent ?? 0) -
              Number(a.labSpecMoistureContent ?? 0);

            if (moistureCompare !== 0) {
              return moistureCompare;
            }

            return 0;
          }

          // ============================================================
          // 3. TUBE WORKSHEET SORTING
          // ============================================================
          if (a.worksheetType === 'Tube' && b.worksheetType === 'Tube') {
            // Sample ID
            const sampleCompare = String(a.sampleId ?? '')
              .localeCompare(
                String(b.sampleId ?? ''),
                undefined,
                { numeric: true, sensitivity: 'base' }
              );

            if (sampleCompare !== 0) {
              return sampleCompare;
            }

            // Depth
            return Number(a.depth ?? 0) - Number(b.depth ?? 0);
          }

          return 0;
        });

        this.dataSource.data = this.worksheets;
        this.dataSource.data.forEach(e => this.updateCalculations(e, true));
        this.rowData = this.dataSource.data;
        this.originalWorksheetData = structuredClone(this.dataSource.data);
      },
      error: (err) => {
        console.error('Error fetching worksheets:', err);
      },
    });
  }
}
